import { useCallback, useRef } from 'react';
import { useContextSelector } from 'use-context-selector';

import type { Vacancy } from 'clients/AutoCompleteClient/types';

import { useGTM } from 'hooks/gtm';
import { useProcessVacancyMutation } from 'shared/legacy/hooks/useProcessVacancyMutation';
import { EnrichTab } from '../components/EditEnrichedVacancyForm/EditEnrichedVacancy.types';
import { EditEnrichedVacancyContext } from '../components/EditEnrichedVacancyForm/EditEnrichedVacancyContext/EditEnrichedVacancyContext';
import { EnrichVacancyModalContext } from '../components/EnrichVacancyModalContext/EnrichVacancyModalContext';
import { EnrichVacancyModalState } from '../components/EnrichVacancyModalContext/EnrichVacancyModalContext.types';

export const useCreateEnrichedVacancy = () => {
  const setState = useContextSelector(EnrichVacancyModalContext, (s) => s.setState);
  const setVacancy = useContextSelector(EnrichVacancyModalContext, (s) => s.setVacancy);
  const setEditVacancyTab = useContextSelector(EnrichVacancyModalContext, (s) => s.setEditVacancyTab);
  const state = useContextSelector(EnrichVacancyModalContext, (s) => s.state);
  const setEnrichedVersionInitialData = useContextSelector(
    EditEnrichedVacancyContext,
    (s) => s.setEnrichedVersionInitialData,
  );
  const setJobPreviewInitialData = useContextSelector(EditEnrichedVacancyContext, (s) => s.setJobPreviewInitialData);
  const gtm = useGTM();

  const { mutateAsync: processVacancy, isLoading: isProcessingVacancy } = useProcessVacancyMutation();

  const hookStatesRef = useRef({ state, isProcessingVacancy, gtm });
  hookStatesRef.current = { state, isProcessingVacancy, gtm };

  const createEnrichedVacancy = useCallback(async (data: Parameters<typeof processVacancy>[0]) => {
    const { state, isProcessingVacancy } = hookStatesRef.current;

    if (isProcessingVacancy || state === EnrichVacancyModalState.EnrichingVacancy) return;

    setState(EnrichVacancyModalState.EnrichingVacancy);

    return processVacancy(data).then((data) => {
      setEnrichedVersionInitialData(data);
      setJobPreviewInitialData(null);

      setState(EnrichVacancyModalState.EditingEnrichedVacancy);
    });
  }, []);

  const handleCreateFromTemplate = useCallback((vacancy: Vacancy) => {
    const { gtm } = hookStatesRef.current;

    setVacancy(vacancy);
    setEditVacancyTab(EnrichTab.EnrichedVersion);
    createEnrichedVacancy(vacancy).finally(() => {
      gtm.createJobUsingTemplate();
    });
  }, []);

  const handleCreateFromJobDescription = useCallback((jobDescription: { subject: string; description: string }) => {
    const { gtm } = hookStatesRef.current;

    createEnrichedVacancy({ text: jobDescription.description }).finally(() => {
      gtm.createJobViaText();
    });
  }, []);

  const handleCreateFromFileUpload = useCallback((file: File) => {
    const { gtm } = hookStatesRef.current;

    createEnrichedVacancy({ file }).finally(() => {
      gtm.createJobUploadingFile();
    });
  }, []);

  return { handleCreateFromTemplate, handleCreateFromJobDescription, handleCreateFromFileUpload };
};
